/* eslint-disable no-unused-vars */
import React, { useState } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  Radio,
  RadioGroup,
  Stack,
  Text,
  VStack,
  Box,
  Badge,
  Flex,
} from '@chakra-ui/react';
import { useGetAllPlans } from '../planManagement/hooks/useGetAllPlans';
import getPlanEndDate from 'constants/helper/getPlanEndDate';

const PlanSelectionModal = ({ isOpen, onClose, onSubmit, isLoading, defaultPlanId, isActive }) => {
  const [selectedPlanId, setSelectedPlanId] = useState('');
  const { data: plans, isLoading: isPlansLoading } = useGetAllPlans();

  React.useEffect(() => {
    if (isOpen && plans && plans.length > 0) {
      if (defaultPlanId) {
        const planExists = plans.some((p) => p._id === defaultPlanId);
        if (planExists) {
          setSelectedPlanId(defaultPlanId);
          return;
        }
      }
      setSelectedPlanId(plans[0]._id);
    } else if (!isOpen) {
      setSelectedPlanId('');
    }
  }, [isOpen, plans, defaultPlanId]);

  const handleSubmit = () => {
    if (selectedPlanId) {
      const selectedPlan = plans.find((plan) => plan._id === selectedPlanId);

      onSubmit(selectedPlan);
      setSelectedPlanId('');
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      isCentered
      size="lg"
      scrollBehavior="inside"
    >
      <ModalOverlay backdropFilter="blur(5px)" />
      <ModalContent borderRadius="xl">
        <ModalHeader>Select Subscription Plan to Activate</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          {isPlansLoading ? (
            <Text>Loading plans...</Text>
          ) : plans?.length === 0 ? (
            <Text>No plans available. Please create a plan first.</Text>
          ) : (
            <RadioGroup onChange={setSelectedPlanId} value={selectedPlanId}>
              <Stack spacing={4}>
                {plans?.map((plan) => {
                  const isSelected = selectedPlanId === plan._id;
                  const isPlanDisabled = isActive && defaultPlanId && defaultPlanId !== plan._id;
                  
                  return (
                    <Box
                      key={plan._id}
                      as="label"
                      cursor={isPlanDisabled ? 'not-allowed' : 'pointer'}
                      opacity={isPlanDisabled ? 0.5 : 1}
                      borderWidth="2px"
                      borderRadius="xl"
                      borderColor={isSelected ? 'teal.500' : 'gray.100'}
                      bg={isSelected ? 'teal.50' : 'white'}
                      boxShadow={isSelected ? 'md' : 'sm'}
                      _hover={
                        isPlanDisabled
                          ? {}
                          : {
                              borderColor: 'teal.300',
                              transform: 'translateY(-2px)',
                              boxShadow: 'md',
                            }
                      }
                      transition="all 0.2s"
                      p={5}
                      position="relative"
                    >
                      <Flex align="start">
                        <Radio
                          value={plan._id}
                          colorScheme="teal"
                          mt={1}
                          isDisabled={isPlanDisabled}
                        />
                        <Box ml={4} flex="1">
                          <Text
                            fontWeight="bold"
                            fontSize="lg"
                            color={isSelected ? 'teal.700' : 'gray.700'}
                            mb={2}
                            textTransform="uppercase"
                          >
                            {plan.name}
                          </Text>
                          <Box
                            fontSize="sm"
                            color="gray.600"
                            sx={{
                              p: { marginBottom: '0.5rem' },
                              'ul, ol': {
                                marginLeft: '1rem',
                                marginBottom: '0.5rem',
                              },
                            }}
                            dangerouslySetInnerHTML={{ __html: plan.details }}
                          />

                          {isSelected && (
                            <Box mt={3} p={3} bg="teal.100" borderRadius="md">
                              <Text
                                fontSize="sm"
                                fontWeight="bold"
                                color="teal.800"
                              >
                                Subscription Period:
                              </Text>
                              <Text fontSize="sm" color="teal.700">
                                {new Date().toLocaleDateString('en-GB', {
                                  day: 'numeric',
                                  month: 'short',
                                  year: 'numeric',
                                })}{' '}
                                - {getPlanEndDate(plan.planCode)}
                              </Text>
                            </Box>
                          )}
                        </Box>
                      </Flex>
                    </Box>
                  );
                })}
              </Stack>
            </RadioGroup>
          )}
        </ModalBody>

        <ModalFooter>
          <Button variant="ghost" mr={3} onClick={onClose}>
            Cancel
          </Button>
          <Button
            colorScheme={isActive ? 'red' : 'teal'}
            onClick={handleSubmit}
            isLoading={isLoading}
            isDisabled={!selectedPlanId}
          >
            {isActive ? 'Deactive User' : 'Activate User'}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default PlanSelectionModal;
